import { type Address, erc20Abi } from 'viem';
import type { UseSwitchChainReturnType } from '@wagmi/vue';
import { readContract, writeContract } from '@wagmi/core';
import EvmProvider, { type EvmProviderInterface } from '@/service/EVM/provider';
import type { EvmContractType } from '@/service/EVM/types';
import type { EvmChainId, EvmMarketplace } from '@/service/EVM/config/types';

/**
 * @param switchNetworkFn - function for network change
 */
export interface TokenWrapperFactoryInterface extends EvmProviderInterface {
    switchNetworkFn: UseSwitchChainReturnType['switchChainAsync'];
}

export default class TokenWrapperService extends EvmProvider implements TokenWrapperFactoryInterface {
    switchNetworkFn;

    constructor({
        eventEmitter,
        config,
        switchNetworkFn,
        chainMap
    }: TokenWrapperFactoryInterface) {
        super({ config, eventEmitter, chainMap });
        this.switchNetworkFn = switchNetworkFn;
    }

    private async handleSwitchNetwork(paramChainId: EvmChainId, currentChainId: EvmChainId) {
        if (paramChainId !== currentChainId) {
            try {
                await this.switchNetworkFn({ chainId: paramChainId });
            }
            catch (e) {
                throw new Error('Switching network error');
            }
        }
    }

    public async approveWrappedToken({
        value,
                                         paramChainId,
                                         marketplaceContractAddress,
                                         currentChainId,
                                         erc20TokenAddress,
                                         userAddress
    }: {
        value: bigint;
        paramChainId: EvmChainId;
        marketplaceContractAddress: Address;
        currentChainId: EvmChainId;
        erc20TokenAddress: Address;
        userAddress: Address;
    }) {
        try {
            await this.handleSwitchNetwork(paramChainId, currentChainId);
        }
        catch (err) {
            const error = err as Error;
            throw new Error(error.message);
        }

        await this.eventEmitter.emit('balance', async () => {
            const weiNetworkBalance = await readContract(this.config, {
                address: erc20TokenAddress,
                chainId: paramChainId,
                functionName: 'balanceOf',
                abi: erc20Abi,
                args: [userAddress]
            });

            if (value > weiNetworkBalance) {
                const currencySymbol = await readContract(this.config, {
                    address: erc20TokenAddress,
                    chainId: paramChainId,
                    functionName: 'symbol',
                    abi: erc20Abi
                });

                throw new Error(JSON.stringify({
                    myBalance: weiNetworkBalance.toString(),
                    needSum: value.toString(),
                    currency: currencySymbol
                }));
            }

            return weiNetworkBalance;
        });

        try {
            await writeContract(this.config, {
                address: erc20TokenAddress,
                functionName: 'approve',
                chainId: paramChainId,
                args: [marketplaceContractAddress, value],
                abi: erc20Abi
            });
        }
        catch (err) {
            throw new Error('Error approving wrapped token');
        }
    }

    public async wrapAndApproveWrappedToken({
        value,
                                                paramChainId,
                                                marketplace,
                                                feeValue,
                                                userAddress,
                                                currentChainId,
                                                bridgeContract,
                                                erc20TokenAddress,
                                                marketplaceContractAddress
    }: {
        value: bigint;
        currentChainId: EvmChainId;
        paramChainId: EvmChainId;
        bridgeContract: EvmContractType;
        marketplace: EvmMarketplace;
        feeValue: number;
        marketplaceContractAddress: Address;
        userAddress: Address;
        erc20TokenAddress: Address;
    }) {
        const weiNetworkBalance = await readContract(this.config, {
            address: erc20TokenAddress,
            chainId: paramChainId,
            functionName: 'balanceOf',
            abi: erc20Abi,
            args: [userAddress]
        }) as bigint;

        if (value > weiNetworkBalance && paramChainId === bridgeContract.chainId && marketplace === 'opensea') {
            throw new Error('Insufficient funds for purchase, please replenish your balance');
        }

        const allFeeValue = (value * BigInt(feeValue * 10)) / 1000n;

        if (value > weiNetworkBalance) {
            try {
                await this.handleSwitchNetwork(paramChainId as EvmChainId, currentChainId);
            }
            catch (err) {
                const error = err as Error;
                throw new Error(error.message);
            }

            const totalValue = (value - weiNetworkBalance) + allFeeValue;

            try {
                await writeContract(this.config, {
                    address: bridgeContract.address,
                    functionName: 'depositEtherFor',
                    abi: bridgeContract.abi,
                    args: [userAddress],
                    value: totalValue
                });
            }
            catch (err) {
                const error = err as Error;
                throw new Error(error.message);
            }

            throw new Error('Token conversion initiated. Please wait for your WETH balance to update.');
        }

        try {
            await this.approveWrappedToken({
                value: value + allFeeValue,
                paramChainId,
                marketplaceContractAddress,
                currentChainId,
                erc20TokenAddress,
                userAddress
            });
        }
        catch (err) {
            const error = err as Error;
            throw new Error(error.message);
        }
    }
}
